import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Stack from '@mui/material/Stack';

/** Matches MUI Button size="large" / ProfileToolbar height. */
const OUTWRITE_BAR_HEIGHT = 42;

const TASKS_ROUTE = '/outwrite/tasks';
const TESTS_ROUTE = '/outwrite/tests';
const LLM_ROUTE = '/outwrite/llm';
const SETUP_ROUTE = '/outwrite/setup';
const LOGS_ROUTE = '/outwrite/logs';

const isOutwriteRoute = (pathname) => pathname.startsWith('/outwrite/');

/**
 * Outwrite chrome group (sibling pattern to BotToolbar / ProfileToolbar).
 * OUTWRITE → TASKS | TESTS | LLM | SETUP | LOGS
 */
const OutwriteToolbar = () => {
  const location = useLocation();
  const routePinned = isOutwriteRoute(location.pathname);
  const [open, setOpen] = useState(routePinned);

  useEffect(() => {
    setOpen(isOutwriteRoute(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (!routePinned) {
          setOpen(false);
        }
      }}
    >
      <Box
        sx={{
          height: OUTWRITE_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          ml: 2,
          pl: 0.5,
          pr: 0.5,
          borderRadius: 2,
          backgroundColor: open
            ? 'rgba(0, 0, 0, 0.22)'
            : 'rgba(0, 0, 0, 0.12)',
          boxShadow: open
            ? '0 4px 14px rgba(0, 0, 0, 0.35), inset 0 0 0 1px rgba(255,255,255,0.18)'
            : '0 1px 4px rgba(0, 0, 0, 0.2), inset 0 0 0 1px rgba(255,255,255,0.1)',
          transition: 'background-color 200ms ease, box-shadow 200ms ease',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ height: OUTWRITE_BAR_HEIGHT }}
        >
          <Button
            variant="contained"
            color={routePinned ? 'warning' : 'secondary'}
            size="large"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-label="Outwrite menu"
            sx={{
              height: OUTWRITE_BAR_HEIGHT,
              whiteSpace: 'nowrap',
              minWidth: 96,
              boxShadow: open ? '0 0 0 2px rgba(255,255,255,0.85)' : 'none',
            }}
          >
            OUTWRITE
          </Button>

          <Collapse
            in={open}
            orientation="horizontal"
            timeout={280}
            unmountOnExit
            sx={{
              height: OUTWRITE_BAR_HEIGHT,
              alignSelf: 'center',
              '& .MuiCollapse-wrapper': {
                height: OUTWRITE_BAR_HEIGHT,
              },
              '& .MuiCollapse-wrapperInner': {
                height: OUTWRITE_BAR_HEIGHT,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ height: OUTWRITE_BAR_HEIGHT, pl: 0.5 }}
            >
              <Button
                variant="contained"
                color={isActive(TASKS_ROUTE) ? 'warning' : 'primary'}
                size="large"
                component={Link}
                to={TASKS_ROUTE}
                sx={{ height: OUTWRITE_BAR_HEIGHT, whiteSpace: 'nowrap' }}
              >
                TASKS
              </Button>
              <Button
                variant="contained"
                color={isActive(TESTS_ROUTE) ? 'warning' : 'primary'}
                size="large"
                component={Link}
                to={TESTS_ROUTE}
                sx={{ height: OUTWRITE_BAR_HEIGHT, whiteSpace: 'nowrap' }}
              >
                TESTS
              </Button>
              <Button
                variant="contained"
                color={isActive(LLM_ROUTE) ? 'warning' : 'primary'}
                size="large"
                component={Link}
                to={LLM_ROUTE}
                sx={{ height: OUTWRITE_BAR_HEIGHT, whiteSpace: 'nowrap' }}
              >
                LLM
              </Button>
              <Button
                variant="contained"
                color={isActive(SETUP_ROUTE) ? 'warning' : 'primary'}
                size="large"
                component={Link}
                to={SETUP_ROUTE}
                sx={{ height: OUTWRITE_BAR_HEIGHT, whiteSpace: 'nowrap' }}
              >
                SETUP
              </Button>
              <Button
                variant="contained"
                color={isActive(LOGS_ROUTE) ? 'warning' : 'primary'}
                size="large"
                component={Link}
                to={LOGS_ROUTE}
                sx={{ height: OUTWRITE_BAR_HEIGHT, whiteSpace: 'nowrap' }}
              >
                LOGS
              </Button>
            </Stack>
          </Collapse>
        </Stack>
      </Box>
    </ClickAwayListener>
  );
};

export default OutwriteToolbar;
